import type { SyncPhase, SyncStatus } from "./SyncEngine";

/** Tag of the Background Sync registration for the outbox (T-073). */
export const backgroundSyncTag = "timetracking-outbox";

/** Time a page gets to answer a synchronization request of the service worker. */
export const clientAnswerTimeoutMs = 20_000;

/** Minimum remaining validity of a mirrored access token for a background upload. */
export const minimumCredentialValidityMs = 60_000;

/** Request of the service worker to an open page: "synchronize now". */
export interface SyncRequestMessage {
  readonly type: "SYNC_REQUEST";
  readonly id: string;
}

/** Answer of a page after its synchronization run. */
export interface SyncResultMessage {
  readonly type: "SYNC_RESULT";
  readonly id: string;
  /** `false` while the server is still unreachable (the browser retries later). */
  readonly ok: boolean;
}

/**
 * Access token mirrored for the service worker, which cannot read the session of the page. Only
 * the short-lived access token is mirrored, never the refresh token (ADR-0034).
 */
export interface BackgroundSyncCredential {
  readonly subject: string;
  /** Tenant chosen by a person with several employers, sent as `X-Tenant-Id` (T-105). */
  readonly tenantId?: string | null;
  readonly accessToken: string;
  /** Expiry of the token in epoch milliseconds. */
  readonly expiresAtMs: number;
}

/** Outcome of a synchronization run in the service worker. */
export type WorkerSyncOutcome = "synced" | "unreachable" | "noCredential";

/**
 * Checks for a {@link SyncRequestMessage}.
 *
 * @param data - Message data.
 * @returns `true` for a request.
 */
export function isSyncRequest(data: unknown): data is SyncRequestMessage {
  const matches =
    typeof data === "object" &&
    data !== null &&
    (data as { readonly type?: unknown }).type === "SYNC_REQUEST" &&
    typeof (data as { readonly id?: unknown }).id === "string";

  return matches;
}

/**
 * Checks for a {@link SyncResultMessage}.
 *
 * @param data - Message data.
 * @returns `true` for a result.
 */
export function isSyncResult(data: unknown): data is SyncResultMessage {
  const matches =
    typeof data === "object" &&
    data !== null &&
    (data as { readonly type?: unknown }).type === "SYNC_RESULT" &&
    typeof (data as { readonly id?: unknown }).id === "string" &&
    typeof (data as { readonly ok?: unknown }).ok === "boolean";

  return matches;
}

/**
 * Whether a finished run left the outbox unsent because the server was unreachable.
 *
 * @param phase - Phase after the run.
 * @returns `true` if a later attempt is needed.
 */
export function needsRetry(phase: SyncPhase): boolean {
  const retry = phase === "offline" || phase === "error";

  return retry;
}

/**
 * Reads the expiry of a JWT access token without verifying it (the server verifies).
 *
 * @param token - The token.
 * @returns The expiry in epoch milliseconds, or `null` if the token has none.
 */
export function tokenExpiryMs(token: string): number | null {
  const payload = token.split(".")[1];

  if (payload === undefined) {
    return null;
  }

  try {
    const json = atob(payload.replace(/-/gu, "+").replace(/_/gu, "/"));
    const exp: unknown = (JSON.parse(json) as { readonly exp?: unknown }).exp;
    const expiry = typeof exp === "number" && Number.isFinite(exp) ? exp * 1000 : null;

    return expiry;
  } catch {
    return null;
  }
}

/**
 * Parses a stored credential.
 *
 * @param value - Stored JSON or `null`.
 * @returns The credential, or `null` if missing or malformed.
 */
export function parseCredential(value: string | null): BackgroundSyncCredential | null {
  if (value === null) {
    return null;
  }

  try {
    const data = JSON.parse(value) as Partial<Record<keyof BackgroundSyncCredential, unknown>>;
    const valid =
      typeof data.subject === "string" &&
      (data.tenantId === undefined ||
        data.tenantId === null ||
        typeof data.tenantId === "string") &&
      typeof data.accessToken === "string" &&
      typeof data.expiresAtMs === "number";
    const credential = valid ? (data as BackgroundSyncCredential) : null;

    return credential;
  } catch {
    return null;
  }
}

/**
 * Returns the credential if it is still valid long enough for an upload.
 *
 * @param credential - The stored credential.
 * @param nowMs - Device time.
 * @returns The credential or `null`.
 */
export function usableCredential(
  credential: BackgroundSyncCredential | null,
  nowMs: number,
): BackgroundSyncCredential | null {
  const usable =
    credential !== null && credential.expiresAtMs - nowMs >= minimumCredentialValidityMs
      ? credential
      : null;

  return usable;
}

/** Dependencies of {@link handleBackgroundSync} (service worker side). */
export interface BackgroundSyncHandlerOptions {
  /**
   * Asks open pages to synchronize. Resolves with the first answer, or `null` if no page
   * answered in time (no page open, or pages frozen in the background).
   */
  readonly askPages: () => Promise<boolean | null>;
  /** Synchronizes in the service worker with the mirrored access token. */
  readonly syncInWorker: () => Promise<WorkerSyncOutcome>;
}

/**
 * Handles a Background Sync event (T-073): an open page synchronizes with its own session; without
 * an answering page the service worker uploads with the mirrored access token. The returned
 * promise rejects while the server is unreachable, so that the browser schedules another attempt;
 * without a usable token it resolves (the next app start synchronizes).
 *
 * @param options - Dependencies.
 * @returns A promise for `event.waitUntil`.
 */
export async function handleBackgroundSync(options: BackgroundSyncHandlerOptions): Promise<void> {
  const answer = await options.askPages();
  const outcome: WorkerSyncOutcome =
    answer === null ? await options.syncInWorker() : answer ? "synced" : "unreachable";

  if (outcome === "unreachable") {
    throw new Error("The server is still unreachable; the browser retries the synchronization.");
  }
}

/** The part of a `SyncManager` the page uses. */
export interface SyncRegistrar {
  register(tag: string): Promise<void>;
}

/** Dependencies of {@link startBackgroundSyncSupport} (page side). */
export interface BackgroundSyncSupportOptions {
  readonly sync: {
    syncNow(): Promise<void>;
    getStatus(): SyncStatus;
    subscribe(listener: (status: SyncStatus) => void): () => void;
  };
  /** Background Sync of the service worker registration, or `null` if unsupported. */
  readonly registrar: () => Promise<SyncRegistrar | null>;
  /** Stores the current access token for the service worker. */
  readonly mirrorCredential: () => Promise<void>;
  /** Subscribes to requests of the service worker; returns an unsubscribe function. */
  readonly onRequest: (
    listener: (request: SyncRequestMessage, reply: (message: SyncResultMessage) => void) => void,
  ) => () => void;
}

/**
 * Page side of Background Sync: when a run ends offline or with an error, the page mirrors its
 * access token and registers a sync, so that the browser (Android, desktop Chromium) retries once
 * the device is online, also after the app was closed. Requests of the service worker are answered
 * with a run of the page's own engine.
 *
 * @param options - Dependencies.
 * @returns A function that stops the support.
 */
export function startBackgroundSyncSupport(options: BackgroundSyncSupportOptions): () => void {
  let previous: SyncPhase = options.sync.getStatus().phase;
  const register = async (): Promise<void> => {
    try {
      const registrar = await options.registrar();

      if (registrar === null) {
        return;
      }

      try {
        await options.mirrorCredential();
      } catch {
        // Without a fresh token only an open page can synchronize; the sync is registered anyway.
      }

      await registrar.register(backgroundSyncTag);
    } catch {
      // Background Sync is an optimization; the foreground triggers still synchronize.
    }
  };
  const stopStatus = options.sync.subscribe((status) => {
    if (needsRetry(status.phase) && !needsRetry(previous)) {
      void register();
    }

    previous = status.phase;
  });
  const stopRequests = options.onRequest((request, reply) => {
    void options.sync
      .syncNow()
      .then(
        () => !needsRetry(options.sync.getStatus().phase),
        () => false,
      )
      .then((ok) => {
        reply({ type: "SYNC_RESULT", id: request.id, ok });
      });
  });
  const stop = (): void => {
    stopStatus();
    stopRequests();
  };

  return stop;
}
